import { LocalParticipant, Participant, RemoteParticipant } from 'livekit-client';
import { participantsStore } from 'state/store';

interface UseParticipantsResult {
    addRemoteParticipant(remoteParticipant: RemoteParticipant): void;
    removeRemoteParticipant(remoteParticipant: RemoteParticipant): void;
    setLocalParticipant(localParticipant: LocalParticipant): void;
    setRemoteParticipants(remoteParticipants: Map<string, RemoteParticipant>): void;
    updateParticipants(remoteParticipants: Participant[]): void;
    setRemoteParticipant(participant: RemoteParticipant): void;
    setLocalParticipantProperty: <K extends keyof LocalParticipant>(property: K, value: LocalParticipant[K]) => void;
    setRemoteParticipantProperty: <K extends keyof RemoteParticipant>(
        identity: string,
        property: K,
        value: RemoteParticipant[K],
    ) => void;
    addScreenShareParticipant(participant: Participant): void;
    removeScreenShareParticipant(participant: Participant): void;
}

export const useParticipants = (): UseParticipantsResult => {
    const addRemoteParticipant = (remoteParticipant: RemoteParticipant) => {
        participantsStore.addRemoteParticipant(remoteParticipant);
    };

    const removeRemoteParticipant = (remoteParticipant: RemoteParticipant) => {
        participantsStore.removeRemoteParticipant(remoteParticipant);
    };

    const setLocalParticipant = (localParticipant: LocalParticipant) => {
        participantsStore.setLocalParticipant(localParticipant);
    };

    const setRemoteParticipants = (remoteParticipants: Map<string, RemoteParticipant>) => {
        participantsStore.setRemoteParticipants(remoteParticipants);
    };

    const updateParticipants = (participants: Participant[]) => {
        const local = participants.find(({ isLocal }) => !!isLocal) as LocalParticipant;
        const remote = participants.filter(({ isLocal }) => !isLocal) as RemoteParticipant[];

        if (local) {
            participantsStore.setLocalParticipant(local);
        }

        if (remote.length > 0) {
            participantsStore.updateRemoteParticipants(remote);
        }
    };

    const setRemoteParticipant = (participant: RemoteParticipant) => {
        participantsStore.updateRemoteParticipant(participant);
    };

    const setLocalParticipantProperty = <K extends keyof LocalParticipant>(property: K, value: LocalParticipant[K]) => {
        participantsStore.setLocalParticipantProperty(property, value);
    };

    const setRemoteParticipantProperty = <K extends keyof RemoteParticipant>(
        identity: string,
        property: K,
        value: RemoteParticipant[K],
    ) => {
        participantsStore.setRemoteParticipantProperty(identity, property, value);
    };

    const addScreenShareParticipant = (participant: Participant) => {
        participantsStore.addScreenShareParticipant(participant);
    };

    const removeScreenShareParticipant = (participant: Participant) => {
        participantsStore.removeScreenShareParticipant(participant);
    };

    return {
        addRemoteParticipant,
        removeRemoteParticipant,
        setLocalParticipant,
        setRemoteParticipants,
        updateParticipants,
        setRemoteParticipant,
        setLocalParticipantProperty,
        setRemoteParticipantProperty,
        addScreenShareParticipant,
        removeScreenShareParticipant,
    };
};
